"use client";

import SectionEyebrow from "@/components/common/sectionEyebrow";
import {
  startTransition,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

const journeyItems = [
  {
    year: "1989",
    description:
      "The consultancy begins as an independent, family-run business created to give UK organisations practical health and safety support.",
  },
  {
    year: "1990s",
    description:
      "Early work with smaller manufacturers helps shape an approach grounded in real operational pressure rather than abstract compliance language.",
  },
  {
    year: "2000s",
    description:
      "The client base broadens well beyond SML's local beginnings as the consultancy starts supporting a wider range of sectors across the UK.",
  },
  {
    year: "Today",
    description:
      "From Daventry, consultants deliver nationwide advisory support, technical input and expert witness consultancy for clients that need clear, proportionate guidance.",
  },
];

export default function JourneyTimelineSection() {
  const itemRefs = useRef([]);
  const frameRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const syncActiveIndex = useEffectEvent(() => {
    if (!itemRefs.current.length) {
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    let nextIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((node, index) => {
      if (!node) {
        return;
      }

      const rect = node.getBoundingClientRect();
      const itemCenter = rect.top + rect.height / 2;
      const distance = Math.abs(itemCenter - viewportCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    startTransition(() => {
      setActiveIndex((currentIndex) =>
        currentIndex === nextIndex ? currentIndex : nextIndex,
      );
    });
  });

  useEffect(() => {
    const handleViewportChange = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        syncActiveIndex();
      });
    };

    handleViewportChange();

    window.addEventListener("scroll", handleViewportChange, { passive: true });
    window.addEventListener("resize", handleViewportChange);

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }

      window.removeEventListener("scroll", handleViewportChange);
      window.removeEventListener("resize", handleViewportChange);
    };
  }, []);

  return (
    <section className="relative py-8 sm:py-10 lg:py-12">
      <div className="relative grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4 lg:sticky lg:top-28 lg:max-w-md">
          <div className="space-y-3">
            <SectionEyebrow
              text="Story"
              className="pt-0"
              textClassName="text-[#06040c]/48"
            />

            <h2 className="font-raleway text-[3rem] font-semibold leading-[0.94] tracking-[-0.06em] text-[#06040c] sm:text-[3.45rem] lg:text-[3.7rem] mb-10">
              Our journey
            </h2>
            <p className="max-w-lg text-base leading-7 text-[#06040c]/58 sm:text-lg">
              From local consultancy roots to nationwide support, each stage of
              SML&apos;s journey reflects a steady focus on practical guidance,
              long-term client relationships and proportionate advice.
            </p>
          </div>
        </div>

        <div className="relative pl-8 sm:pl-10 lg:pl-0">
          <div className="space-y-22 sm:space-y-26">
            {journeyItems.map((item, index) => {
              const isActive = index === activeIndex;

              return (
                <article
                  key={item.year}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  className="relative pl-8 sm:pl-10"
                >
                  {}
                  <span className="absolute left-0 top-3 -translate-x-1/2 flex items-center justify-center">
                    <span className="block h-3.5 w-3.5 rounded-full bg-white" />
                    <span
                      className={`absolute h-3.5 w-3.5 rounded-full transition-colors duration-300 ${
                        isActive ? "bg-[#06040c]" : "bg-[#06040c]/30"
                      }`}
                    />
                  </span>

                  {}
                  {index !== journeyItems.length - 1 && (
                    <div className="absolute left-0 top-7 bottom-[-5.5rem] w-px bg-[#06040c]/18 sm:bottom-[-6.5rem]" />
                  )}

                  <div className="space-y-4">
                    <p
                      className={`font-raleway text-[2.9rem] font-semibold leading-none tracking-[-0.06em] transition-colors duration-300 sm:text-[2.8rem] ${
                        isActive ? "text-[#06040c]" : "text-[#06040c]/38"
                      }`}
                    >
                      {item.year}
                    </p>

                    <p
                      className={`max-w-xl text-base leading-7 transition-colors duration-300 sm:text-md ${
                        isActive ? "text-[#06040c]/68" : "text-[#06040c]/32"
                      }`}
                    >
                      {item.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
